/**
 * SPA-only logging: console-backed, no @repo/logger / winston.
 * Same surface as integrations expect: Logger.debug | warn | error | child().
 *
 * `createIntegrationLogger` must not call `Logger.child` — in production chunks
 * `Logger` can be an uninitialized binding when modules split; use shared helper.
 */
const isDev =
  typeof import.meta !== "undefined" && Boolean(import.meta.env?.DEV);

function makeTimer(label, prefix = "") {
  const start =
    typeof performance !== "undefined" ? performance.now() : Date.now();
  return {
    end: (meta = {}) => {
      const end =
        typeof performance !== "undefined" ? performance.now() : Date.now();
      const ms = end - start;
      if (isDev) {
        console.debug(`${prefix}[Timer] ${label}: ${ms.toFixed(2)}ms`, meta);
      }
      return ms;
    },
  };
}

function createChildLogger(meta = {}) {
  const tag = meta.integration || meta.name || "app";
  const p = `[${tag}]`;
  return {
    log: () => {},
    debug: (...a) => isDev && console.debug(p, ...a),
    info: (...a) => console.info(p, ...a),
    warn: (...a) => console.warn(p, ...a),
    error: (...a) => console.error(p, ...a),
    trace: (...a) => isDev && console.trace?.(p, ...a),
    time: (label) => makeTimer(label, `${p} `),
  };
}

export const Logger = {
  debug: (...args) => {
    if (isDev) console.debug(...args);
  },
  info: (...args) => {
    console.info(...args);
  },
  warn: (...args) => {
    console.warn(...args);
  },
  error: (...args) => {
    console.error(...args);
  },
  trace: (...args) => {
    if (isDev) console.trace?.(...args);
  },
  time: (label) => makeTimer(label, ""),
  child: createChildLogger,
};

/**
 * @param {string} integrationName
 */
export function createIntegrationLogger(integrationName) {
  return createChildLogger({ integration: integrationName });
}
